import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PUBLIC_RECORD, publicRecordState } from '@/features/publicsite/publicRecord';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import { Icon } from '@/features/common/Icon';
import type { TerritorialUnit } from '@/features/common/types/TerritorialUnit';
import { providerPath } from '@/features/publicsite/providerPath';
import type { ServiceProvider } from '@/features/publicsite/types/services/ServiceProvider';
import { serviceTypeLabel } from '@/features/common/serviceType';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';

/**
 * One advisory-services provider's public profile (ToR §6.4, "vizibilitate transparentă...
 * facilitează accesul fermierilor la soluțiile potrivite"), at /servicii/furnizori/{idOrSlug}.
 *
 * Fetches a single resource by slug-or-id and canonicalizes a bare-id link the same way
 * EventDetailPage/ForumPage do — see GET /api/v1/public/providers/{idOrSlug}, slug first, UUID
 * as a fallback for a link shared before slugs existed.
 *
 * Before this page a service's card on /servicii went nowhere — a provider had no place a
 * visitor could open to see its whole portfolio, its coverage, or its place on the map at once.
 */
export function ServiceProviderProfilePage() {
  const { t, language } = useTranslation(publicCatalog);
  const { slug: param = '' } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [missing, setMissing] = useState(false);
  const territorialUnits = usePublicList<TerritorialUnit>(publicCatalogApi.territorialUnitsUrl);

  // Set right before a canonicalizing redirect (below) and consumed by the next run of this
  // effect — same guard EventDetailPage/ForumPage use: without it, navigate() changing the
  // :slug param (which this effect depends on) re-triggers the same fetch for data just loaded.
  const skipNextFetchRef = useRef(false);

  useEffect(() => {
    let live = true;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    setMissing(false);
    publicCatalogApi
      .provider(param)
      .then((p) => {
        if (!live) return;
        setProvider(p);
        // A visitor who arrived on a bare id — an old link, a bookmark, a hand-typed URL — gets
        // the readable address once the slug is known. `replace` so the back button still goes
        // back to the catalogue rather than to the same page under its other name.
        const canonical = providerPath(p);
        if (canonical !== `/servicii/furnizori/${param}`) {
          skipNextFetchRef.current = true;
          navigate(canonical, { replace: true });
        }
      })
      .catch((err) => {
        if (!live) return;
        if (publicRecordState(err) !== PUBLIC_RECORD.missing) {
          console.error('Failed to load provider', err);
        }
        setMissing(true);
      });
    return () => {
      live = false;
    };
  }, [param, navigate]);

  usePublicPageTitle(missing ? t('providerNotFound') : (provider?.name ?? null));

  const back = (
    <Link className="detail-back" to="/servicii">
      <Icon name="chevron-left" className="detail-back-icon" />
      {t('providerBack')}
    </Link>
  );

  if (missing) {
    return (
      <section className="container section">
        {back}
        <p className="eyebrow">{t('eyebrowCatalog')}</p>
        <h1>{t('providerNotFound')}</h1>
      </section>
    );
  }

  if (!provider) {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }

  // Names, not the raw ids the API answers with — a visitor reads "Chișinău, Bălți", not two
  // UUIDs. Resolved against the same public list the self-service registration form already
  // fetches, rather than asking the backend to join and translate a list it otherwise has no
  // reason to carry.
  const zoneNames = provider.coverageZones
    .map((id) => territorialUnits?.find((u) => u.id === id)?.name)
    .filter((name): name is string => !!name);

  return (
    <section className="container section">
      {back}
      <span className="eyebrow">{t('eyebrowCatalog')}</span>
      <h1>{provider.name}</h1>
      <p className="muted">
        {t('providerServiceCount', { count: provider.services.length })}
        {zoneNames.length > 0 && ` · ${t('providerCoverageZones')}: ${zoneNames.join(', ')}`}
      </p>

      {provider.services.length > 0 ? (
        <div className="card-grid">
          {provider.services.map((s, i) => (
            <CatalogCard
              key={`${s.title}-${i}`}
              badge={serviceTypeLabel(s.type, language)}
              title={s.title}
            >
              {s.description && <p>{s.description}</p>}
              <p>
                <Link className="btn btn--ghost" to={`/harta?subjectId=${provider.organizationId}`}>
                  <Icon name="map-pin" className="btn__icon" />
                  {t('providerSeeOnMap')}
                </Link>
              </p>
            </CatalogCard>
          ))}
        </div>
      ) : (
        <p className="empty-state">{t('providerNoServices')}</p>
      )}
    </section>
  );
}
